import React, { useState, useMemo, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  ComposedChart,
  LineChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ReferenceDot,
  ResponsiveContainer,
} from 'recharts';
import FilterDropdown from '../FilterDropdown';
import FilterModal from '../FilterModal';
// ChartHelpButton removed for preset view (no title icon)
import { getCleanOptions, getSmartYDomain, formatDateShort, prepareStackedBarData, getISOWeekNumber, formatLargeNumber, pickBestTerminalCombination, sizeReader, readSizeGroup, initCap } from '../../utils/analyticsHelpers';
import { commoditySizeRules } from './pulse/index.js';
import { toggleMultiValue, shouldNameSeriesForMulti, applyMultiOrAllFilter, packagePinActive } from '../../../utils/filterLabels';
import { multiSelectDropdownLabel } from './terminalPricePulseTableUtils';
import { useChartFontSize } from '../../context/ChartFontSizeContext';
import { useTheme } from '../../../context/ThemeContext';
import { getChartTheme } from '../../utils/chartTheme';
import { useSyncedChartScroll } from './useSyncedChartScroll';
import {
  getLineChartHorizontalScrollDimensions,
  getChartScrollYAxisGutterPx,
  CHART_SCROLL_Y_AXIS_GUTTER_LEFT_MARGIN,
  CHART_SCROLL_Y_AXIS_GUTTER_LEFT_MARGIN_PHONE,
} from './chartHorizontalScrollLayout';
import { getChartYAxisWidth, getYAxisDomainLabels } from './chartStyleUtils';
import usePersistWidgetSnapshot from '../../hooks/usePersistWidgetSnapshot';
import { useWidgetOptionsPersistence } from '../../hooks/useWidgetOptionsPersistence';
import { LinePriceTooltipBody } from './ChartTooltipContent';
import { pulseSkuFromItem, pushPulseSku, pickPackageWithMostRecentMonthPoints, defaultPulseVarieties, VARIETY_SELECT_MAX } from './pulseSkuTooltip';
import { comboSuffix, varyingExtraIndexes, pulseLineColor } from './priceLineSeries';
import { useChartTooltipTrigger } from './useChartTooltipTrigger';
import { FobPricePulseScorecards } from '../analytics/KpiScorecards';
import ChartFullscreenTitleControl from '../analytics/ChartFullscreenTitleControl';
import { PresetChartTitle, PresetFilterStrip, PresetChartLegend, presetChartBodyClass } from '../analytics/PresetChartChrome';
import { useIsBelowLg, useIsPhone, getResponsiveChartMargin } from '../../hooks/useMediaQuery';
import { useDailyVolumeChartData } from '../../../utils/api-hooks/analytics';

const Y_AXIS_TICK_COUNT = 6;
const LEGEND_HEIGHT_PX = 48;
const PULSE_CHART_MARGIN_Y_DEFAULT = 20;
const PULSE_GUTTER_MARGIN_Y_MIN = 2;
const PULSE_GUTTER_MARGIN_Y_MAX = 120;
/** Right-axis overlay: total volume summed by ISO week (organic + conventional), lbs */
const PULSE_VOLUME_DATA_KEY = 'totalVolumeWeek';
/** Weekly total on every row (data key is anchored to one point per week) so the tooltip works on any day */
const PULSE_VOLUME_TOOLTIP_KEY = 'totalVolumeWeekTooltip';
const PULSE_VOLUME_FILL_COLOR = '#FBEDD2';
const PULSE_VOLUME_FILL_OPACITY = 0.5;
/** Top edge + right-axis accent (fill is cream per design) */
const PULSE_VOLUME_STROKE_COLOR = '#C9956B';
const PULSE_X_AXIS_HEIGHT = 60;
/** Tick space for compact volume labels (e.g. 91.7M); do not also inflate margin.right */
const PULSE_VOLUME_Y_AXIS_WIDTH = 52;

/** Volume axis always starts at 0 so area fill meets the shared FOB x-axis baseline. */
function getVolumeYDomainAnchoredAtZero(plotData, dataKey) {
  let max = 0;
  for (const item of plotData) {
    const val = item[dataKey];
    if (typeof val === 'number' && !Number.isNaN(val) && val > max) max = val;
  }
  if (max <= 0) return [0, 'auto'];
  const padding = max * 0.1 || 1;
  return [0, Math.ceil(max + padding)];
}

function formatPriceTick(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return `$${Number(n.toFixed(2))}`;
}

/** Vertical SVG line: true if x is (nearly) constant (column / Y-axis spine / vertical grid). */
function isVerticalSvgLine(line) {
  if (!line || line.tagName !== 'line') return false;
  const x1 = parseFloat(line.getAttribute('x1') || '0');
  const x2 = parseFloat(line.getAttribute('x2') || '0');
  return Number.isFinite(x1) && Number.isFinite(x2) && Math.abs(x1 - x2) < 0.5;
}

function verticalLineSpanPx(line) {
  if (!line) return null;
  const y1 = parseFloat(line.getAttribute('y1') || 'NaN');
  const y2 = parseFloat(line.getAttribute('y2') || 'NaN');
  if (!Number.isFinite(y1) || !Number.isFinite(y2)) return null;
  return Math.abs(y2 - y1);
}

/**
 * The single-select characteristic filters, in the order the UI cascades them.
 * Origins sit above the whole chain.
 */
const PRICE_LINE_CASCADE_TAIL = [
  'storage', 'condition', 'environment', 'quality', 'appearance', 'properties',
];
const PRICE_LINE_CASCADE_ORDER = [
  'type', 'variety', 'size', 'grade', 'package', ...PRICE_LINE_CASCADE_TAIL,
];
const PRICE_LINE_CASCADE_CUCUMBER = [
  'type', 'variety', 'sizeGroup', 'package', 'size', 'grade', ...PRICE_LINE_CASCADE_TAIL,
];

const PRICE_LINE_MULTI_KEYS = new Set(['variety', 'sizeGroup', 'size', 'grade', 'storage', 'condition', 'environment', 'quality', 'appearance', 'properties']);

const priceLineField = (key, readSize) => {
  if (key === 'size') return readSize || 'item_size';
  if (key === 'sizeGroup') return 'size_group';
  return key;
};

const readCascadeValue = (item, key, readSize) => {
  const field = priceLineField(key, readSize);
  if (typeof field === 'function') return field(item);
  if (key === 'package') return item.package || item.sku;
  return item[field];
};

/** Restrict rows by one cascade filter, matching how the chart itself filters. */
const applyPriceLineCascadeFilter = (rows, key, value, readSize, sizeRules) => {
  if (Array.isArray(value)) {
    if (sizeRules && key === 'size') {
      const opts = getCleanOptions(rows, readSize || 'item_size');
      return sizeRules.applySizeFilter(rows, value, opts, readSize);
    }
    if (sizeRules && key === 'sizeGroup') {
      const groupRead = sizeRules.readSizeGroup || readSizeGroup;
      const opts = getCleanOptions(rows, groupRead);
      return sizeRules.applySizeGroupFilter(rows, value, opts, groupRead);
    }
    if (!value.length) return rows;
    return rows.filter((item) => value.includes(readCascadeValue(item, key, readSize)));
  }
  if (!value) return rows;
  switch (key) {
    case 'type': return rows.filter((item) => !item.type || item.type === value);
    case 'package': return rows.filter((item) => item.package === value || item.sku === value);
    default: return rows.filter((item) => readCascadeValue(item, key, readSize) === value);
  }
};

/**
 * Price Pulse Chart - Shows price trends by district
 */
const PriceLineChart = ({
  data = [],
  email = '',
  dateStart = null,
  dateEnd = null,
  commodity = '',
  forceFiltersOpen = false,
  presetScorecards = null,
  hidePresetScorecards = false,
  presetDescription = '',
  hideLegend = false,
  analyticsFullscreen = null,
  presetSingleChartFullscreen = false,
  fitXToWidth = false,
  presetViewId,
}) => {
  const isAdvancedAnalyticsView = presetViewId === 'view2';
  const sizeRules = commoditySizeRules(commodity);
  const readSize = sizeRules?.readSize || sizeReader(commodity);
  const cascadeOrder = sizeRules ? PRICE_LINE_CASCADE_CUCUMBER : PRICE_LINE_CASCADE_ORDER;
  const { isReady: analyticsPrefsReady } = useWidgetOptionsPersistence('price-line');
  const { t } = useTranslation();
  const { fontSize } = useChartFontSize();
  const { isDark } = useTheme();
  const theme = getChartTheme(isDark);
  // Collapsed by default. In fullscreen the filters still show on desktop (lg+)
  // via a CSS override below, but on small screens (e.g. the portrait panel)
  // they stay collapsed behind the Filters toggle for a cleaner view.
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const scrollViewportRef = useRef(null);
  useSyncedChartScroll(scrollViewportRef, 'price-pulse-scroll');
  const { trigger: tooltipTrigger, tooltipDismissKey, tooltipActive, tooltipPosition, tooltipSnap, tooltipCursorX, tooltipPinPayload, pinTooltip, markTapStart } = useChartTooltipTrigger(scrollViewportRef);
  const isBelowLg = useIsBelowLg();
  const isPhone = useIsPhone();

  // Filter states
  const [selectedPriceTypes, setSelectedPriceTypes] = useState(['low_price']);
  const [priceUnit, setPriceUnit] = useState('package'); // 'package', 'kg', 'lbs'
  const [selectedType, setSelectedType] = useState('Conventional');
  const [selectedVariety, setSelectedVariety] = useState([]);
  const [selectedOrigins, setSelectedOrigins] = useState([]);
  const [selectedSizeGroup, setSelectedSizeGroup] = useState([]);
  const [selectedSize, setSelectedSize] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState('');
  const lastPackageRef = useRef('');
  const [selectedStorage, setSelectedStorage] = useState([]);
  const [selectedCondition, setSelectedCondition] = useState([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState([]);
  const [selectedQuality, setSelectedQuality] = useState([]);
  const [selectedAppearance, setSelectedAppearance] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);
  /** True only when the user explicitly cleared all origins (Clear All). Distinguishes from incomplete first persist [] before defaults. */
  const [originsUserCleared, setOriginsUserCleared] = useState(false);
  const [showVolume, setShowVolume] = useState(false);

  useEffect(() => {
    if (!isAdvancedAnalyticsView) setShowVolume(false);
  }, [isAdvancedAnalyticsView]);

  const volumeOverlayActive = isAdvancedAnalyticsView && showVolume;

  const { volumeChart, isLoading: volumeChartLoading } = useDailyVolumeChartData(
    email,
    commodity,
    dateStart,
    dateEnd,
    { enabled: Boolean(volumeOverlayActive && commodity) }
  );

  const preparedVolumeRows = useMemo(() => prepareStackedBarData(volumeChart || []), [volumeChart]);

  const linePriceTooltipContent = useCallback(
    (props) => (
      <LinePriceTooltipBody
        {...(tooltipSnap || props)}
        theme={theme}
        fontSize={fontSize}
        commodity={commodity}
        volumeLineDataKey={volumeOverlayActive ? PULSE_VOLUME_DATA_KEY : undefined}
        volumeTooltipDataKey={volumeOverlayActive ? PULSE_VOLUME_TOOLTIP_KEY : undefined}
        volumeSeriesColor={PULSE_VOLUME_STROKE_COLOR}
      />
    ),
    [theme, fontSize, volumeOverlayActive, tooltipSnap, commodity]
  );

  // Modal states
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [varietyModalOpen, setVarietyModalOpen] = useState(false);
  const [originModalOpen, setOriginModalOpen] = useState(false);
  const [sizeGroupModalOpen, setSizeGroupModalOpen] = useState(false);
  const [sizeModalOpen, setSizeModalOpen] = useState(false);
  const [gradeModalOpen, setGradeModalOpen] = useState(false);
  const [packageModalOpen, setPackageModalOpen] = useState(false);
  const [priceUnitModalOpen, setPriceUnitModalOpen] = useState(false);
  const [storageModalOpen, setStorageModalOpen] = useState(false);
  const [conditionModalOpen, setConditionModalOpen] = useState(false);
  const [environmentModalOpen, setEnvironmentModalOpen] = useState(false);
  const [qualityModalOpen, setQualityModalOpen] = useState(false);
  const [appearanceModalOpen, setAppearanceModalOpen] = useState(false);
  const [propertiesModalOpen, setPropertiesModalOpen] = useState(false);

  const initializedRef = useRef(false);

  // When commodity changes, allow defaults/hydrate to run for the new scope.
  // This must be a layout effect so it runs before the init effect below.
  useLayoutEffect(() => {
    initializedRef.current = false;
  }, [commodity]);

  // Check if price unit fields are available
  const priceUnitFieldsAvailable = useMemo(() => {
    if (!data || data.length === 0) return false;
    // Check if at least one item has all price unit fields
    return data.some((item) =>
      item.low_price_lbs != null &&
      item.high_price_lbs != null &&
      item.low_price_kg != null &&
      item.high_price_kg != null
    );
  }, [data]);

  // Cascade order: Origins (district) → Type → Variety → Size → Grade → Package → Storage → Condition → Environment → Quality → Appearance → Properties
  const availableOptions = useMemo(() => {
    if (!data || data.length === 0) {
      return { types: [], varieties: [], origins: [], sizeGroups: [], sizes: [], grades: [], packages: [],
               storages: [], conditions: [], environments: [], qualities: [],
               appearances: [], properties: [] };
    }

    const origins = getCleanOptions(data, 'district');
    let filtered = data;
    if (selectedOrigins.length > 0) {
      filtered = filtered.filter((item) => item.district && selectedOrigins.includes(item.district));
    }
    const types = getCleanOptions(filtered, 'type');
    if (selectedType) filtered = filtered.filter((item) => !item.type || item.type === selectedType);
    const varieties = getCleanOptions(filtered, 'variety', commodity);
    filtered = applyMultiOrAllFilter(filtered, 'variety', selectedVariety, varieties);
    let sizeGroups = [];
    let sizes = [];
    if (sizeRules) {
      const groupRead = sizeRules.readSizeGroup;
      sizeGroups = getCleanOptions(filtered, groupRead, commodity);
      sizes = getCleanOptions(filtered, readSize, commodity);
      filtered = sizeRules.applySizeGroupFilter(filtered, selectedSizeGroup, sizeGroups, groupRead);
      filtered = sizeRules.applySizeFilter(filtered, selectedSize, sizes, readSize);
    }
    let packages = [];
    const pinPackages = () => {
      if (!packagePinActive(priceUnit)) return;
      packages = [...new Set(filtered.map((d) => d.package || d.sku || '').filter((v) => v))].sort();
      if (selectedPackage) {
        filtered = filtered.filter((item) => item.package === selectedPackage || item.sku === selectedPackage);
      }
    };
    if (sizeRules) pinPackages();
    if (!sizeRules) {
      sizes = getCleanOptions(filtered, 'item_size', commodity);
      filtered = applyMultiOrAllFilter(filtered, 'item_size', selectedSize, sizes);
    }
    const grades = getCleanOptions(filtered, 'grade', commodity);
    filtered = applyMultiOrAllFilter(filtered, 'grade', selectedGrade, grades);
    if (!sizeRules) pinPackages();
    const storages = getCleanOptions(filtered, 'storage', commodity);
    filtered = applyMultiOrAllFilter(filtered, 'storage', selectedStorage, storages);
    const conditions = getCleanOptions(filtered, 'condition', commodity);
    filtered = applyMultiOrAllFilter(filtered, 'condition', selectedCondition, conditions);
    const environments = getCleanOptions(filtered, 'environment', commodity);
    filtered = applyMultiOrAllFilter(filtered, 'environment', selectedEnvironment, environments);
    const qualities = getCleanOptions(filtered, 'quality', commodity);
    filtered = applyMultiOrAllFilter(filtered, 'quality', selectedQuality, qualities);
    const appearances = getCleanOptions(filtered, 'appearance', commodity);
    filtered = applyMultiOrAllFilter(filtered, 'appearance', selectedAppearance, appearances);
    const properties = getCleanOptions(filtered, 'properties', commodity);
    filtered = applyMultiOrAllFilter(filtered, 'properties', selectedProperties, properties);

    return { types, varieties, origins, sizeGroups, sizes, grades, packages,
             storages, conditions, environments, qualities,
             appearances, properties };
  }, [data, selectedOrigins, selectedType, selectedVariety, selectedSizeGroup, selectedSize, selectedGrade, selectedPackage,
      selectedStorage, selectedCondition, selectedEnvironment, selectedQuality, selectedAppearance, selectedProperties, priceUnit, sizeRules, commodity]);

  useEffect(() => {
    if (!sizeRules) return;
    const nextSize = sizeRules.keepOrAll(selectedSize, availableOptions.sizes || []);
    if (JSON.stringify(nextSize) !== JSON.stringify(selectedSize)) setSelectedSize(nextSize);
    const nextGroup = sizeRules.keepOrAll(selectedSizeGroup, availableOptions.sizeGroups || []);
    if (JSON.stringify(nextGroup) !== JSON.stringify(selectedSizeGroup)) setSelectedSizeGroup(nextGroup);
  }, [availableOptions.sizes, availableOptions.sizeGroups, selectedSize, selectedSizeGroup, sizeRules]);

  /**
   * Re-derive every filter below `level` so the chart lands on the most data
   * available inside the user's choice.
   *
   * These filters used to be kept whenever the old value still appeared in the
   * new dropdown ("keep-if-valid"). Dropdown membership is not coverage: a Size
   * and SKU that merely exist under a newly picked Type can intersect it on
   * only a handful of days, leaving the chart nearly empty while every filter
   * still looks reasonable. Everything below the changed filter is now
   * re-scored on days actually plotted, the same way the initial default is
   * chosen — so a reload reproduces what was on screen.
   *
   * @param {string|null} level — the filter the user changed; null means
   *   origins, which sit above the whole chain
   * @param {object} [overrides] — values not yet flushed to state, keyed as in
   *   PRICE_LINE_CASCADE_ORDER plus `origins`
   */
  const cascadeBelow = useCallback((level, overrides = {}) => {
    if (!data || data.length === 0) return;

    const current = {
      type: selectedType,
      variety: selectedVariety,
      sizeGroup: selectedSizeGroup,
      size: selectedSize,
      grade: selectedGrade,
      package: selectedPackage,
      storage: selectedStorage,
      condition: selectedCondition,
      environment: selectedEnvironment,
      quality: selectedQuality,
      appearance: selectedAppearance,
      properties: selectedProperties,
    };
    const valueFor = (key) => (overrides[key] !== undefined ? overrides[key] : current[key]);

    const origins = overrides.origins !== undefined ? overrides.origins : selectedOrigins;
    let rows = data;
    if (origins.length > 0) {
      rows = rows.filter((item) => item.district && origins.includes(item.district));
    }

    // Pin the changed filter and everything above it, then score the rest.
    const cut = level === null ? 0 : cascadeOrder.indexOf(level) + 1;
    for (let i = 0; i < cut; i++) {
      const key = cascadeOrder[i];
      const unit = overrides.priceUnit !== undefined ? overrides.priceUnit : priceUnit;
      if (key === 'package' && unit !== 'package') continue;
      rows = applyPriceLineCascadeFilter(rows, key, valueFor(key), readSize, sizeRules);
    }

    const priceTypes = selectedPriceTypes.length > 0 ? selectedPriceTypes : ['low_price', 'high_price'];
    const best = pickBestTerminalCombination(rows, { priceTypes });

    const setters = {
      type: setSelectedType,
      variety: setSelectedVariety,
      sizeGroup: setSelectedSizeGroup,
      size: setSelectedSize,
      grade: setSelectedGrade,
      package: setSelectedPackage,
      storage: setSelectedStorage,
      condition: setSelectedCondition,
      environment: setSelectedEnvironment,
      quality: setSelectedQuality,
      appearance: setSelectedAppearance,
      properties: setSelectedProperties,
    };
    for (let i = cut; i < cascadeOrder.length; i++) {
      const key = cascadeOrder[i];
      if (key === 'package') continue;
      if (sizeRules && key === 'size' && level === 'sizeGroup') continue;
      if (PRICE_LINE_MULTI_KEYS.has(key)) {
        const nextOpts = getCleanOptions(
          rows,
          key === 'sizeGroup' ? sizeRules.readSizeGroup : priceLineField(key, readSize),
          commodity
        );
        if (sizeRules && (key === 'size' || key === 'sizeGroup')) {
          setters[key](sizeRules.keepOrAll(valueFor(key), nextOpts));
        } else {
          setters[key](nextOpts);
        }
      } else {
        const cur = valueFor(key);
        const still = cur && rows.some((item) => {
          if (key === 'package') return item.package === cur || item.sku === cur;
          return readCascadeValue(item, key, readSize) === cur;
        });
        setters[key](still ? cur : (best[key] || ''));
      }
    }
  }, [
    data, selectedOrigins, selectedPriceTypes, priceUnit, cascadeOrder, readSize, commodity,
    selectedType, selectedVariety, selectedSizeGroup, selectedSize, selectedGrade, selectedPackage,
    selectedStorage, selectedCondition, selectedEnvironment, selectedQuality,
    selectedAppearance, selectedProperties, sizeRules,
  ]);

  // Cascade from origins: re-derive Type through Properties for coverage.
  // Accepts an optional originsOverride so callers can pass the freshly-computed
  // next origins value without waiting for the React state update to flush.
  const cascadeFromOrigins = useCallback((originsOverride) => {
    cascadeBelow(null, originsOverride !== undefined ? { origins: originsOverride } : {});
  }, [cascadeBelow]);

  // Switching price unit back to package restores the SKU the user last had,
  // when it still exists in scope; everything below it is re-scored.
  const restorePackageSelection = useCallback(() => {
    if (!data || data.length === 0) return;
    let rows = data;
    if (selectedOrigins.length > 0) {
      rows = rows.filter((item) => item.district && selectedOrigins.includes(item.district));
    }
    const upstream = {
      type: selectedType, variety: selectedVariety, sizeGroup: selectedSizeGroup, size: selectedSize, grade: selectedGrade,
    };
    for (const key of cascadeOrder.filter((k) => k !== 'package' && PRICE_LINE_CASCADE_TAIL.indexOf(k) === -1)) {
      rows = applyPriceLineCascadeFilter(rows, key, upstream[key], readSize, sizeRules);
    }
    const nextPackage = pickPackageWithMostRecentMonthPoints(rows);
    setSelectedPackage(nextPackage);
    cascadeBelow('package', { package: nextPackage, priceUnit: 'package' });
  }, [data, selectedOrigins, selectedType, selectedVariety, selectedSizeGroup, selectedSize, selectedGrade, cascadeBelow, cascadeOrder, readSize]);

  // Do NOT clear initializedRef in useEffect([data]): effects run after useLayoutEffect, so that
  // would run after Firestore hydrate (usePersistWidgetSnapshot) and wipe initializedRef,
  // letting the default cascade below overwrite saved state and persist it back to Firebase.

  const hydratePriceLine = useCallback(
    (s) => {
      initializedRef.current = true;
      if (typeof s.filtersExpanded === 'boolean') setFiltersExpanded(s.filtersExpanded);
      const validPt = ['low_price', 'high_price'];
      const savedPt = Array.isArray(s.selectedPriceTypes)
        ? s.selectedPriceTypes.filter((p) => validPt.includes(p))
        : [];
      const priceTypes = savedPt.length > 0 ? savedPt : ['low_price', 'high_price'];
      if (savedPt.length > 0) setSelectedPriceTypes(savedPt);
      setPriceUnit('kg');

      const allDistricts = getCleanOptions(data, 'district');
      const cleared = s.originsUserCleared === true;
      setOriginsUserCleared(cleared);
      let origins;
      if (cleared) {
        origins = Array.isArray(s.selectedOrigins) ? s.selectedOrigins.filter((o) => allDistricts.includes(o)) : [];
      } else if (Array.isArray(s.selectedOrigins) && s.selectedOrigins.length > 0) {
        origins = s.selectedOrigins.filter((o) => allDistricts.includes(o));
      } else {
        // Missing key, junk persist before defaults, or [] without explicit clear → all districts
        origins = allDistricts;
      }
      setSelectedOrigins(origins);

      let rows = data;
      if (origins.length > 0) {
        rows = rows.filter((item) => item.district && origins.includes(item.district));
      }

      // Every load opens on the combination with the most data, ignoring the
      // characteristics saved from the last visit. Restoring them meant a user
      // who had drilled into a thin slice (say Organic on a commodity that is
      // mostly Conventional) came back to a nearly empty chart and read it as
      // broken. Filtering is a within-session action: pick a type, see that
      // type; reload, and the chart is full again.
      const restored = pickBestTerminalCombination(rows, { priceTypes });

      setSelectedType(restored.type || 'Conventional');
      {
        let scoped = rows;
        if (restored.type) scoped = scoped.filter((item) => !item.type || item.type === restored.type);
        setSelectedVariety(defaultPulseVarieties(scoped));
        if (sizeRules) {
          const allG = getCleanOptions(scoped, sizeRules.readSizeGroup, commodity);
          const allS = getCleanOptions(scoped, readSize, commodity);
          setSelectedSizeGroup(sizeRules.keepOrAll(
            Array.isArray(s.selectedSizeGroup) ? s.selectedSizeGroup : [],
            allG,
          ));
          setSelectedSize(sizeRules.keepOrAll(
            Array.isArray(s.selectedSize) ? s.selectedSize : [],
            allS,
          ));
        } else {
          setSelectedSizeGroup([]);
          setSelectedSize(getCleanOptions(scoped, 'item_size', commodity));
        }
        setSelectedGrade(getCleanOptions(scoped, 'grade'));
        setSelectedStorage(getCleanOptions(scoped, 'storage'));
        setSelectedCondition(getCleanOptions(scoped, 'condition'));
        setSelectedEnvironment(getCleanOptions(scoped, 'environment'));
        setSelectedQuality(getCleanOptions(scoped, 'quality'));
        setSelectedAppearance(getCleanOptions(scoped, 'appearance'));
        setSelectedProperties(getCleanOptions(scoped, 'properties'));
      }
      setSelectedPackage(restored.package);

      if (isAdvancedAnalyticsView && typeof s.showVolume === 'boolean') setShowVolume(s.showVolume);

      // First-open / junk snapshot: if origins were missing (but not explicitly cleared),
      // apply deterministic defaults based on "most datapoints" (matches init effect).
      if (!cleared && (!Array.isArray(s.selectedOrigins) || s.selectedOrigins.length === 0)) {
        const defaultPriceTypes = ['low_price', 'high_price'];
        setSelectedPriceTypes(defaultPriceTypes);
        setPriceUnit('kg');

        // Same scoring as the init effect, so a junk snapshot lands on the same
        // defaults a first-time load would have produced.
        const best = pickBestTerminalCombination(data, { priceTypes: defaultPriceTypes });
        setSelectedType(best.type || 'Conventional');
        {
          let scoped = data;
          if (best.type) scoped = scoped.filter((item) => !item.type || item.type === best.type);
          setSelectedVariety(defaultPulseVarieties(scoped));
          if (sizeRules) {
            const allG = getCleanOptions(scoped, sizeRules.readSizeGroup, commodity);
            const allS = getCleanOptions(scoped, readSize, commodity);
            setSelectedSizeGroup(sizeRules.keepOrAll(
              Array.isArray(s.selectedSizeGroup) ? s.selectedSizeGroup : [],
              allG,
            ));
            setSelectedSize(sizeRules.keepOrAll(
              Array.isArray(s.selectedSize) ? s.selectedSize : [],
              allS,
            ));
          } else {
            setSelectedSizeGroup([]);
            setSelectedSize(getCleanOptions(scoped, 'item_size', commodity));
          }
          setSelectedGrade(getCleanOptions(scoped, 'grade'));
          setSelectedStorage(getCleanOptions(scoped, 'storage'));
          setSelectedCondition(getCleanOptions(scoped, 'condition'));
          setSelectedEnvironment(getCleanOptions(scoped, 'environment'));
          setSelectedQuality(getCleanOptions(scoped, 'quality'));
          setSelectedAppearance(getCleanOptions(scoped, 'appearance'));
          setSelectedProperties(getCleanOptions(scoped, 'properties'));
        }
        setSelectedPackage(best.package);
      }
    },
    [data, sizeRules, commodity]
  );

  const snapshotPriceLine = useCallback(
    () => ({
      filtersExpanded,
      selectedPriceTypes,
      priceUnit,
      selectedType,
      selectedVariety,
      selectedOrigins,
      originsUserCleared,
      selectedSizeGroup,
      selectedSize,
      selectedGrade,
      selectedPackage,
      selectedStorage,
      selectedCondition,
      selectedEnvironment,
      selectedQuality,
      selectedAppearance,
      selectedProperties,
      showVolume,
    }),
    [
      filtersExpanded,
      selectedPriceTypes,
      priceUnit,
      selectedType,
      selectedVariety,
      selectedOrigins,
      originsUserCleared,
      selectedSizeGroup,
      selectedSize,
      selectedGrade,
      selectedPackage,
      selectedStorage,
      selectedCondition,
      selectedEnvironment,
      selectedQuality,
      selectedAppearance,
      selectedProperties,
      showVolume,
    ]
  );

  usePersistWidgetSnapshot(
    'price-line',
    commodity,
    !!(data && data.length > 0),
    hydratePriceLine,
    snapshotPriceLine,
    [
      filtersExpanded,
      selectedPriceTypes,
      priceUnit,
      selectedType,
      selectedVariety,
      selectedOrigins,
      originsUserCleared,
      selectedSizeGroup,
      selectedSize,
      selectedGrade,
      selectedPackage,
      selectedStorage,
      selectedCondition,
      selectedEnvironment,
      selectedQuality,
      selectedAppearance,
      selectedProperties,
      showVolume,
    ]
  );

  // Defaults only when there is no Firestore snapshot to hydrate; after analytics state is fetched.
  useEffect(() => {
    if (!analyticsPrefsReady || !data || data.length === 0) return;
    if (initializedRef.current) return;

    initializedRef.current = true;
    setOriginsUserCleared(false);

    // Default: all price types selected; all origins; characteristics chosen by
    // whichever combination plots the most days.
    const defaultPriceTypes = ['low_price', 'high_price'];
    setSelectedPriceTypes(defaultPriceTypes);
    setPriceUnit('kg');

    // Set all origins by default
    const allOrigins = getCleanOptions(data, 'district');
    setSelectedOrigins(allOrigins);

    // Score whole combinations rather than taking the most common value one
    // field at a time. Greedy chaining optimises each field against the slice
    // the previous one left, and over eleven dependent fields that lands on a
    // thin cell — and because a blank value was never a candidate, any field
    // that is mostly unset threw away the rows that made up the bulk of the
    // data. Type is scored here too (Conventional only breaks exact ties).
    const best = pickBestTerminalCombination(data, { priceTypes: defaultPriceTypes });
    setSelectedType(best.type || 'Conventional');
    {
      let scoped = data;
      if (best.type) scoped = scoped.filter((item) => !item.type || item.type === best.type);
      setSelectedVariety(defaultPulseVarieties(scoped));
      if (sizeRules) {
        setSelectedSizeGroup(getCleanOptions(scoped, sizeRules.readSizeGroup, commodity));
        setSelectedSize(getCleanOptions(scoped, readSize, commodity));
      } else {
        setSelectedSizeGroup([]);
        setSelectedSize(getCleanOptions(scoped, 'item_size', commodity));
      }
      setSelectedGrade(getCleanOptions(scoped, 'grade'));
      setSelectedStorage(getCleanOptions(scoped, 'storage'));
      setSelectedCondition(getCleanOptions(scoped, 'condition'));
      setSelectedEnvironment(getCleanOptions(scoped, 'environment'));
      setSelectedQuality(getCleanOptions(scoped, 'quality'));
      setSelectedAppearance(getCleanOptions(scoped, 'appearance'));
      setSelectedProperties(getCleanOptions(scoped, 'properties'));
    }
    setSelectedPackage(best.package);
  }, [analyticsPrefsReady, data]);

  // Filter and transform data for chart
  const { chartData, lineKeys, seriesTotal, seriesCapped } = useMemo(() => {
    if (!data || data.length === 0 || selectedOrigins.length === 0 || selectedPriceTypes.length === 0) {
      return { chartData: [], lineKeys: [], seriesTotal: 0, seriesCapped: false };
    }

    const getPriceField = (item, priceTypeValue) => {
      if (priceUnit === 'package') {
        return item[priceTypeValue];
      } else if (priceUnit === 'kg') {
        return priceTypeValue === 'low_price' ? item.low_price_kg : item.high_price_kg;
      } else if (priceUnit === 'lbs') {
        return priceTypeValue === 'low_price' ? item.low_price_lbs : item.high_price_lbs;
      }
      return item[priceTypeValue];
    };

    const filteredData = data.filter((item) => {
      if (item.type && selectedType && item.type !== selectedType) return false;
      if (shouldNameSeriesForMulti(selectedVariety, availableOptions.varieties) && !selectedVariety.includes(item.variety)) return false;
      if (sizeRules && !sizeRules.sizeGroupKeepsRow(item, selectedSizeGroup, availableOptions.sizeGroups, sizeRules.readSizeGroup)) return false;
      if (sizeRules) {
        if (!sizeRules.sizeKeepsRow(item, selectedSize, availableOptions.sizes, readSize)) return false;
      } else if (shouldNameSeriesForMulti(selectedSize, availableOptions.sizes) && !selectedSize.includes(item.item_size)) return false;
      if (shouldNameSeriesForMulti(selectedGrade, availableOptions.grades) && !selectedGrade.includes(item.grade)) return false;
      if (priceUnit === 'package' && selectedPackage && (item.package !== selectedPackage && item.sku !== selectedPackage)) return false;
      if (selectedOrigins.length > 0 && !selectedOrigins.includes(item.district)) return false;
      if (shouldNameSeriesForMulti(selectedStorage, availableOptions.storages) && !selectedStorage.includes(item.storage)) return false;
      if (shouldNameSeriesForMulti(selectedCondition, availableOptions.conditions) && !selectedCondition.includes(item.condition)) return false;
      if (shouldNameSeriesForMulti(selectedEnvironment, availableOptions.environments) && !selectedEnvironment.includes(item.environment)) return false;
      if (shouldNameSeriesForMulti(selectedQuality, availableOptions.qualities) && !selectedQuality.includes(item.quality)) return false;
      if (shouldNameSeriesForMulti(selectedAppearance, availableOptions.appearances) && !selectedAppearance.includes(item.appearance)) return false;
      if (shouldNameSeriesForMulti(selectedProperties, availableOptions.properties) && !selectedProperties.includes(item.properties)) return false;
      return true;
    });

    const months = {
      Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
      Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
    };

    const toLocalMidnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

    const dayKeyFromDate = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    };

    const parseDateLabel = (str) => {
      const parts = String(str || '').trim().split(/\s+/);
      if (parts.length < 2) return null;
      const day = parseInt(parts[0], 10);
      const month = months[parts[1]];
      if (Number.isNaN(day) || month === undefined) return null;
      const year = parts.length >= 3 ? parseInt(parts[2], 10) : null;
      const yearValid = year != null && !Number.isNaN(year) ? year : null;
      return { day, month, year: yearValid };
    };

    const inferYearForMonthDay = (month, day, start, end) => {
      const startY = start.getFullYear();
      let candidate = toLocalMidnight(new Date(startY, month, day));
      if (candidate < start) candidate = toLocalMidnight(new Date(startY + 1, month, day));
      if (candidate > end) candidate = toLocalMidnight(new Date(startY - 1, month, day));
      return candidate;
    };

    const sortKeyFromLabel = (str) => {
      const p = parseDateLabel(str);
      if (!p) return 0;
      const y = p.year ?? new Date().getFullYear();
      return new Date(y, p.month, p.day).getTime();
    };

    const lineKeySet = new Set();
    const skuSeen = new Set();
    const hasRange = !!dateStart && !!dateEnd;
    const extraSeries = (item) => comboSuffix(item, priceUnit === 'package');

    if (hasRange) {
      const start = toLocalMidnight(new Date(dateStart));
      const end = toLocalMidnight(new Date(dateEnd));

      const dateMap = new Map();
      let cur = new Date(start);
      while (cur <= end) {
        const key = dayKeyFromDate(cur);
        const dayCopy = new Date(cur);
        dateMap.set(key, {
          date: formatDateShort(dayCopy),
          sortKey: dayCopy.getTime(),
          rawDate: dayCopy,
        });
        cur.setDate(cur.getDate() + 1);
      }

      filteredData.forEach((item) => {
        const district = item.district || 'Unknown';
        const parsed = parseDateLabel(item.date_string);
        if (!parsed) return;

        let candidateDate;
        if (parsed.year != null) {
          candidateDate = toLocalMidnight(new Date(parsed.year, parsed.month, parsed.day));
          if (candidateDate < start || candidateDate > end) {
            candidateDate = inferYearForMonthDay(parsed.month, parsed.day, start, end);
          }
        } else {
          candidateDate = inferYearForMonthDay(parsed.month, parsed.day, start, end);
        }

        const key = dayKeyFromDate(candidateDate);
        const dateEntry = dateMap.get(key);
        if (!dateEntry) return;

        selectedPriceTypes.forEach((priceTypeValue) => {
          const priceValue = getPriceField(item, priceTypeValue);
          if (typeof priceValue === 'number' && !Number.isNaN(priceValue)) {
            const lineKey = `${district}_${priceTypeValue}${extraSeries(item)}`;
            lineKeySet.add(lineKey);
            pushPulseSku(dateEntry, lineKey, pulseSkuFromItem(item, priceTypeValue, priceValue), skuSeen);
            if (dateEntry[lineKey] !== undefined) {
              dateEntry[lineKey] = (dateEntry[lineKey] + priceValue) / 2;
            } else {
              dateEntry[lineKey] = priceValue;
            }
          }
        });
      });

      const keys = Array.from(lineKeySet);
      return {
        chartData: Array.from(dateMap.values()).sort((a, b) => a.sortKey - b.sortKey),
        lineKeys: keys,
        seriesTotal: keys.length,
        seriesCapped: false,
      };
    }

    const dateMap = new Map();
    filteredData.forEach((item) => {
      const dateKey = item.date_string;
      const district = item.district || 'Unknown';

      if (!dateMap.has(dateKey)) {
        const sortKey = sortKeyFromLabel(dateKey);
        dateMap.set(dateKey, { date: dateKey, sortKey, rawDate: sortKey ? new Date(sortKey) : null });
      }

      selectedPriceTypes.forEach((priceTypeValue) => {
        const priceValue = getPriceField(item, priceTypeValue);
        if (typeof priceValue === 'number' && !Number.isNaN(priceValue)) {
          const lineKey = `${district}_${priceTypeValue}${extraSeries(item)}`;
          lineKeySet.add(lineKey);
          const dateEntry = dateMap.get(dateKey);
          pushPulseSku(dateEntry, lineKey, pulseSkuFromItem(item, priceTypeValue, priceValue), skuSeen);
          if (dateEntry[lineKey] !== undefined) {
            dateEntry[lineKey] = (dateEntry[lineKey] + priceValue) / 2;
          } else {
            dateEntry[lineKey] = priceValue;
          }
        }
      });
    });

    const keys = Array.from(lineKeySet);
    return {
      chartData: Array.from(dateMap.values()).sort((a, b) => a.sortKey - b.sortKey),
      lineKeys: keys,
      seriesTotal: keys.length,
      seriesCapped: false,
    };
  }, [data, selectedPriceTypes, priceUnit, selectedType, selectedVariety, selectedOrigins, selectedSizeGroup, selectedSize, selectedGrade, selectedPackage,
      selectedStorage, selectedCondition, selectedEnvironment, selectedQuality, selectedAppearance, selectedProperties, dateStart, dateEnd, availableOptions, sizeRules]);

  const chartDataWithVolume = useMemo(() => {
    if (!volumeOverlayActive) return chartData;
    if (!chartData?.length) return chartData;

    // Ticket scope: commodity comes from the chart API; include only organic + conventional
    // (do not mirror pulse district/variety/SKU filters — those often use different field names
    // and would zero out the overlay).
    const volRowIncluded = (item) => {
      const ty = String(item?.type ?? '').trim().toLowerCase();
      return ty === 'organic' || ty === 'conventional';
    };

    const weekNum = (item) => {
      if (item.iso_week != null) return Number(item.iso_week);
      if (item.iso_week_number != null) return Number(item.iso_week_number);
      const d = item.date_string instanceof Date ? item.date_string : new Date(item.date_string);
      return getISOWeekNumber(d);
    };

    const isoYearFromItem = (item) => {
      if (item.iso_year != null) return Number(item.iso_year);
      const d = item.date_string instanceof Date ? item.date_string : new Date(item.date_string);
      const month = d.getMonth();
      const iw = weekNum(item);
      if (iw === 1 && month === 11) return d.getFullYear() + 1;
      if (iw >= 52 && month === 0) return d.getFullYear() - 1;
      return item.year != null ? Number(item.year) : d.getFullYear();
    };

    const weekTotals = new Map();
    for (const item of preparedVolumeRows) {
      if (!volRowIncluded(item)) continue;
      const y = isoYearFromItem(item);
      const w = weekNum(item);
      const key = `${y}_${w}`;
      const add = Number(item.vol ?? item.volume ?? item.daily_vol ?? 0) || 0;
      weekTotals.set(key, (weekTotals.get(key) || 0) + add);
    }

    const weekKeyFromDate = (d) => {
      if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
      const isoWeek = getISOWeekNumber(d);
      const month = d.getMonth();
      let isoYear = d.getFullYear();
      if (isoWeek === 1 && month === 11) isoYear = d.getFullYear() + 1;
      if (isoWeek >= 52 && month === 0) isoYear = d.getFullYear() - 1;
      return `${isoYear}_${isoWeek}`;
    };

    // One point per week (at the week's midpoint row), null elsewhere — the
    // monotone Area with connectNulls then draws a smooth curve instead of
    // daily plateaus. First/last rows are also anchored so the fill spans the
    // full chart width. The tooltip key keeps the weekly total on every row.
    const rowWeekKeys = chartData.map((row) => {
      const d = row.rawDate instanceof Date ? row.rawDate : typeof row.sortKey === 'number' ? new Date(row.sortKey) : null;
      return weekKeyFromDate(d);
    });

    const weekRowIndices = new Map();
    rowWeekKeys.forEach((wk, i) => {
      if (wk == null) return;
      if (!weekRowIndices.has(wk)) weekRowIndices.set(wk, []);
      weekRowIndices.get(wk).push(i);
    });

    const anchorIndices = new Set();
    for (const indices of weekRowIndices.values()) {
      anchorIndices.add(indices[Math.floor(indices.length / 2)]);
    }
    anchorIndices.add(0);
    anchorIndices.add(chartData.length - 1);

    return chartData.map((row, i) => {
      const wk = rowWeekKeys[i];
      const total = wk != null ? weekTotals.get(wk) : undefined;
      const v = total !== undefined && Number.isFinite(total) ? total : null;
      return {
        ...row,
        [PULSE_VOLUME_DATA_KEY]: anchorIndices.has(i) ? v : null,
        [PULSE_VOLUME_TOOLTIP_KEY]: v,
      };
    });
  }, [chartData, volumeOverlayActive, preparedVolumeRows]);

  const plotData = chartDataWithVolume;

  // Add half-day padding on both sides so duration matches Volume-by-Origin bars
  const xAxisConfig = useMemo(() => {
    if (!plotData || plotData.length === 0) {
      return { ticks: undefined, domain: undefined };
    }
    const xs = plotData
      .map((d) => (typeof d.sortKey === 'number' ? d.sortKey : null))
      .filter((v) => typeof v === 'number' && !Number.isNaN(v));
    if (xs.length === 0) return { ticks: undefined, domain: undefined };

    const min = Math.min(...xs);
    const max = Math.max(...xs);
    const halfDayMs = 12 * 60 * 60 * 1000;
    const ticks = plotData
      .filter((d) => d.date && typeof d.sortKey === 'number')
      .map((d) => d.sortKey);

    return { ticks, domain: [min - halfDayMs, max + halfDayMs] };
  }, [plotData]);

  const priceYDomain = useMemo(
    () => getSmartYDomain(plotData, lineKeys),
    [plotData, lineKeys]
  );

  // Phones size the price axis to the labels it actually draws instead of the
  // fixed 72px, which is mostly empty at the smaller phone tick size.
  //
  // Preset views only: the widget dashboard renders a rotated axis title in
  // that same column (the `!forceFiltersOpen` branches below). Rotated about
  // its centre, that title's horizontal extent sits mid-column, so narrowing
  // the column would run it straight into the tick labels.
  const compactYAxis = isPhone && forceFiltersOpen;
  const priceYAxisWidth = useMemo(
    () =>
      getChartYAxisWidth({
        isPhone: compactYAxis,
        fontSize,
        labels: getYAxisDomainLabels(priceYDomain, formatPriceTick),
      }),
    [compactYAxis, fontSize, priceYDomain]
  );
  const scrollYAxisGutterPx = getChartScrollYAxisGutterPx({
    isPhone: compactYAxis,
    yAxisWidth: priceYAxisWidth,
  });

  const hasVolumeOverlay = useMemo(
    () =>
      volumeOverlayActive &&
      plotData.some((d) => {
        const v = d[PULSE_VOLUME_DATA_KEY];
        return v != null && Number.isFinite(Number(v));
      }),
    [plotData, volumeOverlayActive]
  );

  const volumeYDomain = useMemo(
    () =>
      volumeOverlayActive && hasVolumeOverlay
        ? getVolumeYDomainAnchoredAtZero(plotData, PULSE_VOLUME_DATA_KEY)
        : [0, 'auto'],
    [volumeOverlayActive, hasVolumeOverlay, plotData]
  );

  // Recharts reserves width on the right YAxis; extra margin.right stacked on top and caused a large empty gutter.
  const mainChartRightMargin = hasVolumeOverlay ? 8 : 20;

  const priceAxisLabelText = useMemo(
    () =>
      priceUnit === 'package'
        ? t('filters.price')
        : `${t('filters.price')} ($${priceUnit === 'kg' ? '/kg' : '/lb'})`,
    [t, priceUnit]
  );

  const plotAreaRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = plotAreaRef.current;
    if (!el) return;

    let rafId = null;
    let rafRetryId = null;
    const updateWidth = (next) => {
      const width = typeof next === 'number' ? next : el.getBoundingClientRect().width;
      if (!Number.isFinite(width) || width <= 0) return;
      setContainerWidth((prev) => (Math.abs(prev - width) < 0.5 ? prev : width));
    };

    // Ensure we measure after layout settles (important when navigating into Analytics)
    rafId = window.requestAnimationFrame(() => updateWidth());
    // Retry a few frames until we get a non-zero width (some layouts report 0 on first paint)
    let attempts = 0;
    const retry = () => {
      attempts += 1;
      updateWidth();
      if ((el.getBoundingClientRect().width || 0) <= 0 && attempts < 10) {
        rafRetryId = window.requestAnimationFrame(retry);
      }
    };
    rafRetryId = window.requestAnimationFrame(retry);

    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver((entries) => {
        const entry = entries[0];
        const w = entry?.contentRect?.width;
        updateWidth(w);
      });
      ro.observe(el);
    }

    window.addEventListener('resize', updateWidth);
    return () => {
      if (rafId != null) window.cancelAnimationFrame(rafId);
      if (rafRetryId != null) window.cancelAnimationFrame(rafRetryId);
      window.removeEventListener('resize', updateWidth);
      if (ro) ro.disconnect();
    };
  }, []);

  const { needsScroll: needsScrollRaw, chartWidth, minWidthNeeded } = useMemo(
    () =>
      getLineChartHorizontalScrollDimensions({
        dataPointCount: plotData.length,
        containerWidth,
      }),
    [plotData.length, containerWidth]
  );
  const needsScroll = needsScrollRaw && !fitXToWidth;

  const effectiveMinWidth = useMemo(() => {
    const mw = Number(minWidthNeeded) || 0;
    const cw = Number(containerWidth) || 0;
    const v = Math.max(mw, cw);
    return v > 0 ? v : undefined;
  }, [minWidthNeeded, containerWidth]);

  /** Gutter Y-axis chart: vertical margins tuned so axis spine length matches main chart vertical grid lines. */
  const pulseGridChartRef = useRef(null);
  const pulseYAxisChartRef = useRef(null);
  const [gutterPlotMarginsY, setGutterPlotMarginsY] = useState({
    top: PULSE_CHART_MARGIN_Y_DEFAULT,
    bottom: PULSE_CHART_MARGIN_Y_DEFAULT,
  });

  const pulseYAxisSyncLayoutKey = useMemo(
    () =>
      [
        needsScroll,
        chartWidth,
        containerWidth,
        plotData.length,
        lineKeys.join('\u0000'),
        `${priceYDomain?.[0]}-${priceYDomain?.[1]}`,
        `${volumeYDomain?.[0]}-${volumeYDomain?.[1]}`,
        volumeOverlayActive,
        fontSize,
        filtersExpanded,
      ].join('|'),
    [
      needsScroll,
      chartWidth,
      containerWidth,
      plotData.length,
      lineKeys,
      priceYDomain,
      volumeYDomain,
      volumeOverlayActive,
      fontSize,
      filtersExpanded,
    ]
  );

  useLayoutEffect(() => {
    if (!needsScroll) {
      setGutterPlotMarginsY((p) =>
        p.top === PULSE_CHART_MARGIN_Y_DEFAULT && p.bottom === PULSE_CHART_MARGIN_Y_DEFAULT
          ? p
          : { top: PULSE_CHART_MARGIN_Y_DEFAULT, bottom: PULSE_CHART_MARGIN_Y_DEFAULT }
      );
      return;
    }
    setGutterPlotMarginsY((p) =>
      p.top === PULSE_CHART_MARGIN_Y_DEFAULT && p.bottom === PULSE_CHART_MARGIN_Y_DEFAULT
        ? p
        : { top: PULSE_CHART_MARGIN_Y_DEFAULT, bottom: PULSE_CHART_MARGIN_Y_DEFAULT }
    );
  }, [needsScroll, pulseYAxisSyncLayoutKey]);

  useLayoutEffect(() => {
    if (!needsScroll) return;

    let cancelled = false;
    const measureAndSync = () => {
      if (cancelled) return;
      const gridRoot = pulseGridChartRef.current;
      const axisRoot = pulseYAxisChartRef.current;
      if (!gridRoot || !axisRoot) return;

      const gridLines = gridRoot.querySelectorAll('.recharts-cartesian-grid-vertical line');
      let gridLine = null;
      for (let i = 0; i < gridLines.length; i++) {
        if (isVerticalSvgLine(gridLines[i])) {
          gridLine = gridLines[i];
          break;
        }
      }

      const axisLines = axisRoot.querySelectorAll('line.recharts-cartesian-axis-line');
      let axisLine = null;
      for (let i = 0; i < axisLines.length; i++) {
        if (isVerticalSvgLine(axisLines[i])) {
          axisLine = axisLines[i];
          break;
        }
      }

      const gridSpan = gridLine ? verticalLineSpanPx(gridLine) : null;
      const axisSpan = axisLine ? verticalLineSpanPx(axisLine) : null;
      if (gridSpan == null || axisSpan == null) return;

      const lengthDiff = gridSpan - axisSpan;
      if (Number.isFinite(lengthDiff) && Math.abs(lengthDiff) >= 0.5) {
        setGutterPlotMarginsY((prev) => {
          const half = lengthDiff / 2;
          const top = Math.min(
            PULSE_GUTTER_MARGIN_Y_MAX,
            Math.max(PULSE_GUTTER_MARGIN_Y_MIN, prev.top - half)
          );
          const bottom = Math.min(
            PULSE_GUTTER_MARGIN_Y_MAX,
            Math.max(PULSE_GUTTER_MARGIN_Y_MIN, prev.bottom - half)
          );
          if (Math.abs(top - prev.top) < 0.25 && Math.abs(bottom - prev.bottom) < 0.25) return prev;
          return { top, bottom };
        });
        return;
      }

      // Lengths match: align top (and bottom) endpoints in viewport — shift gutter chart vertically
      // without changing margin top+bottom sum, so spine length stays matched.
      const gTop = gridLine.getBoundingClientRect().top;
      const aTop = axisLine.getBoundingClientRect().top;
      const deltaTop = aTop - gTop;
      if (!Number.isFinite(deltaTop) || Math.abs(deltaTop) < 0.5) return;

      setGutterPlotMarginsY((prev) => {
        const nextTop = prev.top - deltaTop;
        const nextBottom = prev.bottom + deltaTop;
        const top = Math.min(
          PULSE_GUTTER_MARGIN_Y_MAX,
          Math.max(PULSE_GUTTER_MARGIN_Y_MIN, nextTop)
        );
        const bottom = Math.min(
          PULSE_GUTTER_MARGIN_Y_MAX,
          Math.max(PULSE_GUTTER_MARGIN_Y_MIN, nextBottom)
        );
        if (Math.abs(top - prev.top) < 0.25 && Math.abs(bottom - prev.bottom) < 0.25) return prev;
        return { top, bottom };
      });
    };

    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(measureAndSync);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [needsScroll, pulseYAxisSyncLayoutKey, gutterPlotMarginsY.top, gutterPlotMarginsY.bottom]);

  // Handle origin selection (toggle); cascade runs immediately with the new origins
  // so downstream dropdowns and chart data stay in sync without waiting for modal close.
  const handleOriginChange = (origin) => {
    let next;
    if (selectedOrigins.includes(origin)) {
      next = selectedOrigins.filter((o) => o !== origin);
    } else {
      next = [...selectedOrigins, origin];
    }
    setSelectedOrigins(next);
    setOriginsUserCleared(next.length === 0);
    cascadeFromOrigins(next);
  };

  // Handle price type selection (toggle behavior for multi-select)
  const handlePriceTypeChange = (priceType) => {
    if (selectedPriceTypes.includes(priceType)) {
      setSelectedPriceTypes(selectedPriceTypes.filter((p) => p !== priceType));
    } else {
      setSelectedPriceTypes([...selectedPriceTypes, priceType]);
    }
  };

  // Get display name for chart line (combines district and price type)
  const extraVary = useMemo(() => varyingExtraIndexes(lineKeys), [lineKeys]);

  const getLineDisplayName = (lineKey) => {
    const [main, ...extras] = String(lineKey).split('::');
    let district = main;
    let priceTypeLabel = '';

    if (main.endsWith('_low_price')) {
      district = main.slice(0, -10);
      priceTypeLabel = t('filters.lowPrice');
    } else if (main.endsWith('_high_price')) {
      district = main.slice(0, -11);
      priceTypeLabel = t('filters.highPrice');
    }

    const extra = extras.filter((bit, i) => bit && extraVary[i]).join(' - ');
    const extraBit = extra ? ` - ${extra}` : '';
    if (selectedPriceTypes.length === 1) {
      return `${district}${extraBit}`;
    }
    return `${district} - ${priceTypeLabel}${extraBit}`;
  };

  const helpDescription = "Daily price trends showing low, high, and average prices over the selected period. Filter by type, variety, origin, and other attributes to analyze specific market segments.";

  // Single-chart fullscreen: below lg the overlay scrolls, so these boxes grow
  // with their content instead of being squeezed to 0px, and the plot keeps a
  // real height. See the `.preset-fullscreen-*` block in index.css.
  const fsFill = presetSingleChartFullscreen ? ' preset-fullscreen-fill' : '';
  const fsPlot = presetSingleChartFullscreen ? ' preset-fullscreen-plot' : '';

  if (!data || data.length === 0) {
    return (
      <div className="h-full flex flex-col bg-transparent">
        <PresetChartTitle
          forceFiltersOpen={forceFiltersOpen}
          title={t('charts.pricePulse.title')}
          theme={theme}
          fontSize={fontSize}
          className={forceFiltersOpen ? 'px-4 pt-4' : 'border-y py-2 px-4'}
        />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-center" style={{ fontSize, color: theme.noDataText }}>
            {t('charts.pricePulse.noData')}<br />{t('charts.selectAnotherTimeframe')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        (forceFiltersOpen
          ? presetSingleChartFullscreen
            ? 'w-full h-full min-h-0 flex-1' + fsFill
            : 'w-full'
          : 'h-full') + ' flex flex-col bg-transparent'
      }
    >
      <div className={`flex-1 flex flex-col min-h-0${fsFill} ${presetChartBodyClass(forceFiltersOpen, presetSingleChartFullscreen)}`}>
      {/* Title */}
      <PresetChartTitle
        forceFiltersOpen={forceFiltersOpen}
        title={t('charts.pricePulse.title')}
        theme={theme}
        fontSize={fontSize}
        right={
          <ChartFullscreenTitleControl
            analyticsFullscreen={analyticsFullscreen}
            presetSingleChartFullscreen={presetSingleChartFullscreen}
          />
        }
      />

      {/* Filters / Cards / Chart */}
        {/* Filters */}
        <div className="flex-shrink-0">
          {/* Filters Header — collapsible on small screens even in fullscreen
              (portrait panel); lg:hidden once forceFiltersOpen since desktop
              fullscreen keeps filters open via the CSS gate below. */}
          <button
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className={`w-full flex items-center justify-between py-2 border-b flex-shrink-0 ${forceFiltersOpen ? 'lg:hidden' : ''}`}
            style={{ borderColor: theme.borderSecondary }}
          >
            <span className="font-medium" style={{ fontSize: fontSize - 2, color: theme.secondaryText }}>{t('filters.filters')}</span>
            {filtersExpanded ? (
              <ChevronUp size={16} style={{ color: theme.secondaryText }} />
            ) : (
              <ChevronDown size={16} style={{ color: theme.secondaryText }} />
            )}
          </button>

          {/* Filter Dropdowns — preset view puts them on one scrollable line
              with the Fullscreen action pinned to the right of the strip.
              In fullscreen, stay mounted but hidden below lg unless the user
              expanded them (max-lg:hidden gate); lg+ always shows. */}
          {(forceFiltersOpen || filtersExpanded) && (
            <div className={forceFiltersOpen ? (filtersExpanded ? 'max-lg:pt-3' : 'max-lg:hidden') : undefined}>
            <PresetFilterStrip
              forceFiltersOpen={forceFiltersOpen}
              presetSingleChartFullscreen={presetSingleChartFullscreen}
              analyticsFullscreen={analyticsFullscreen}
              titleHasFullscreenControl
            >
            <FilterDropdown
              label={t('filters.findPrice')}
              options={['low_price', 'high_price']}
              selected={selectedPriceTypes}
              allLabel={t('filters.allPrices')}
              placeholder={t('filters.selectPriceType')}
              getOptionLabel={(opt) => opt === 'low_price' ? t('filters.lowPrice') : t('filters.highPrice')}
              onClick={() => setPriceModalOpen(true)}
              compact={forceFiltersOpen}
            />

            {/* Cascade order: Origins → Type → Variety → Size → Grade → Package →
                Storage → Condition → Environment → Quality → Appearance → Properties */}
            {availableOptions.origins.length > 0 && (
              <FilterDropdown
                label={t('filters.origin')}
                options={availableOptions.origins}
                selected={selectedOrigins}
                allLabel={t('filters.allOrigins')}
                placeholder={t('filters.selectOrigins')}
                onClick={() => setOriginModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}

            {availableOptions.types.length > 0 && (
              <FilterDropdown
                label={t('filters.type')}
                value={selectedType || t('filters.selectType')}
                onClick={() => setTypeModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}

            {availableOptions.varieties.length > 0 && (
              <FilterDropdown
                label={t('filters.variety')}
                value={multiSelectDropdownLabel(selectedVariety, availableOptions.varieties, {
                  allLabel: t('filters.allVarieties'),
                  nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                  emptyLabel: t('filters.selectVariety'),
                })}
                onClick={() => setVarietyModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}

            {sizeRules && availableOptions.sizeGroups.length > 0 && (
              <FilterDropdown
                label={t('filters.sizeGroup', 'Size Group')}
                value={multiSelectDropdownLabel(selectedSizeGroup, availableOptions.sizeGroups, {
                  allLabel: t('filters.allSizeGroups', 'All Size Groups'),
                  nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                  emptyLabel: t('filters.noSizeGroup', 'No size group'),
                })}
                onClick={() => setSizeGroupModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}

            {sizeRules && priceUnit === 'package' && availableOptions.packages.length > 0 && (
              <FilterDropdown
                label={t('filters.sku')}
                value={selectedPackage || t('filters.selectSku')}
                onClick={() => setPackageModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}

            {availableOptions.sizes.length > 0 && (
              <FilterDropdown
                label={sizeRules ? t('filters.sizeGranularity', 'Size granularity') : t('filters.size')}
                value={multiSelectDropdownLabel(selectedSize, availableOptions.sizes, {
                  allLabel: sizeRules ? t('filters.allSizeGranularities', 'All size granularities') : t('filters.allSizes'),
                  nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                  emptyLabel: sizeRules ? t('filters.noSizeGranularity', 'No size granularity') : t('filters.selectSize'),
                })}
                onClick={() => setSizeModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}

            {availableOptions.grades.length > 0 && (
              <FilterDropdown
                label={t('filters.grade')}
                value={multiSelectDropdownLabel(selectedGrade, availableOptions.grades, {
                  allLabel: t('filters.allGrades'),
                  nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                  emptyLabel: t('filters.selectGrade'),
                })}
                onClick={() => setGradeModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}

            {!sizeRules && priceUnit === 'package' && availableOptions.packages.length > 0 && (
              <FilterDropdown
                label={t('filters.sku')}
                value={selectedPackage || t('filters.selectSku')}
                onClick={() => setPackageModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}

            {availableOptions.storages.length > 0 && (
              <FilterDropdown
                label={t('filters.storage')}
                value={multiSelectDropdownLabel(selectedStorage, availableOptions.storages, {
                  allLabel: t('filters.allStorage'),
                  nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                  emptyLabel: t('filters.select'),
                })}
                onClick={() => setStorageModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}
            {availableOptions.conditions.length > 0 && (
              <FilterDropdown
                label={t('filters.condition')}
                value={multiSelectDropdownLabel(selectedCondition, availableOptions.conditions, {
                  allLabel: t('filters.allCondition', 'All Conditions'),
                  nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                  emptyLabel: t('filters.select'),
                })}
                onClick={() => setConditionModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}
            {availableOptions.environments.length > 0 && (
              <FilterDropdown
                label={t('filters.environment')}
                value={multiSelectDropdownLabel(selectedEnvironment, availableOptions.environments, {
                  allLabel: t('filters.allEnvironment', 'All Environments'),
                  nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                  emptyLabel: t('filters.select'),
                })}
                onClick={() => setEnvironmentModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}
            {availableOptions.qualities.length > 0 && (
              <FilterDropdown
                label={t('filters.quality')}
                value={multiSelectDropdownLabel(selectedQuality, availableOptions.qualities, {
                  allLabel: t('filters.allQuality'),
                  nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                  emptyLabel: t('filters.select'),
                })}
                onClick={() => setQualityModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}
            {availableOptions.appearances.length > 0 && (
              <FilterDropdown
                label={t('filters.appearance')}
                value={multiSelectDropdownLabel(selectedAppearance, availableOptions.appearances, {
                  allLabel: t('filters.allAppearance', 'All Appearances'),
                  nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                  emptyLabel: t('filters.select'),
                })}
                onClick={() => setAppearanceModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}
            {availableOptions.properties.length > 0 && (
              <FilterDropdown
                label={t('filters.properties')}
                value={multiSelectDropdownLabel(selectedProperties, availableOptions.properties, {
                  allLabel: t('filters.allProperties'),
                  nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                  emptyLabel: t('filters.select'),
                })}
                onClick={() => setPropertiesModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}

            {priceUnitFieldsAvailable && (
              <FilterDropdown
                label={t('filters.priceUnit')}
                value={priceUnit === 'package' ? t('filters.package') : priceUnit === 'kg' ? t('filters.perKg') : t('filters.perLb')}
                onClick={() => setPriceUnitModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}

            {isAdvancedAnalyticsView ? (
              <div className={forceFiltersOpen ? 'min-w-0 lg:shrink-0' : undefined}>
                <label
                  className={
                    forceFiltersOpen
                      ? 'mb-[4px] block text-[11px] font-medium text-[#828382] dark:text-gray-400'
                      : 'block mb-1 font-medium'
                  }
                  style={forceFiltersOpen ? undefined : { fontSize: fontSize - 2, color: theme.secondaryText }}
                >
                  {t('charts.pricePulse.showVolumeLabel')}
                </label>
                <button
                  type="button"
                  onClick={() => setShowVolume(!showVolume)}
                  disabled={!commodity}
                  className={
                    forceFiltersOpen
                      ? `flex h-[27px] w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-[8px] px-[10px] text-[11px] font-medium transition-colors lg:w-auto lg:justify-start ${
                          showVolume
                            ? 'bg-orange-500 text-white'
                            : 'border border-[#E2E2E2] text-[#282A29] hover:border-bloom-green dark:border-[#3a3c3b] dark:text-white'
                        }`
                      : `w-full py-1.5 px-3 rounded-lg font-medium transition-colors ${showVolume ? 'bg-orange-500 text-white' : ''}`
                  }
                  style={
                    forceFiltersOpen
                      ? undefined
                      : {
                          fontSize: fontSize - 2,
                          ...(showVolume
                            ? {}
                            : {
                                backgroundColor: theme.controlBg,
                                borderColor: theme.controlBorder,
                                color: theme.controlText,
                                border: '1px solid',
                              }),
                        }
                  }
                >
                  {volumeChartLoading && showVolume
                    ? t('charts.loading')
                    : showVolume
                      ? t('charts.pricePulse.volumeShown')
                      : t('charts.pricePulse.volumeHidden')}
                </button>
              </div>
            ) : null}
            </PresetFilterStrip>
            </div>
          )}


        </div>

      {/* Cards (View 1 KPIs) */}
      {forceFiltersOpen && !hidePresetScorecards ? (
        <div className="flex-shrink-0">
          <FobPricePulseScorecards
            apiParams={{ email, commodity, dateStart, dateEnd }}
            dims={{
              type: selectedType,
              variety: selectedVariety,
              origin: selectedOrigins,
              size: selectedSize,
              grade: selectedGrade,
              package: selectedPackage,
              storage: selectedStorage,
              condition: selectedCondition,
              environment: selectedEnvironment,
              quality: selectedQuality,
              appearance: selectedAppearance,
              properties: selectedProperties,
              ...(priceUnit ? { price_unit: priceUnit } : {}),
              ...(selectedPriceTypes.length === 1 ? { price_type: selectedPriceTypes[0] } : {}),
            }}
          />
        </div>
      ) : presetScorecards ? (
        <div className="flex-shrink-0">{presetScorecards}</div>
      ) : null}

        {/* Chart */}
      <div
        className={
          (forceFiltersOpen && !presetSingleChartFullscreen ? '' : 'flex-1 min-h-0' + fsFill) +
          ' flex flex-col overflow-visible bg-white dark:bg-[#191919] rounded-lg'
        }
      >
        {/* Fixed-height plot box (`preset-chart-plot`). The legend is a sibling
            of this box, not a child: it is flex-shrink-0, so a many-series
            legend (e.g. 5 origins x low/high on a phone, one row each) would
            consume the whole box and squeeze the flex-1 plot to 0px — the chart
            would silently vanish while its legend still rendered. Keeping the
            legend outside lets the card grow instead of starving the plot. */}
        <div
          className={
            (forceFiltersOpen && !presetSingleChartFullscreen
              ? 'preset-chart-plot'
              : 'flex-1' + fsPlot) + ' min-h-0 flex flex-col'
          }
        >
        {presetDescription ? (
          <div className="px-4 pt-4 pb-3 flex-shrink-0">
            {/* What the plotted series is currently filtered to. The graph name
                itself lives in the card title above, so it is not repeated here. */}
            <p className="text-[12px] text-[#828382] dark:text-gray-400 text-center">
              {(() => {
                const commodityLabel = commodity
                  ? commodity
                      .split(' ')
                      .filter(Boolean)
                      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                      .join(' ')
                  : 'this commodity';

                const isMeaningful = (v) => v != null && v !== '' && v !== 'N/A' && v !== 'Not Classified';
                const parts = [];

                if (isMeaningful(selectedType)) parts.push(selectedType);
                if (shouldNameSeriesForMulti(selectedVariety, availableOptions.varieties)) {
                  parts.push(selectedVariety.filter(isMeaningful).join(', '));
                }
                if (priceUnit) {
                  parts.push(priceUnit === 'package' ? t('filters.package') : priceUnit === 'kg' ? t('filters.perKg') : t('filters.perLb'));
                }

                const summary = parts.filter(Boolean).join(' · ');
                return summary ? `${commodityLabel}, ${summary}` : commodityLabel;
              })()}
            </p>
          </div>
        ) : null}
        <div className="flex-1 min-h-0 flex flex-row overflow-hidden">
          {plotData.length > 0 && lineKeys.length > 0 && needsScroll ? (
            <div
              className="relative flex-shrink-0 h-full"
              style={{
                width: scrollYAxisGutterPx,
                borderColor: theme.borderSecondary,
                backgroundColor: theme.widgetBg,
              }}
            >
              {/* Hide y-axis label in preset view (matches design) */}
              {!forceFiltersOpen && (
                <span
                  className="pointer-events-none absolute z-[1] left-1 top-1/2 -translate-y-1/2 origin-center -rotate-90 whitespace-nowrap"
                  style={{ fontSize, color: theme.axisLabelFill }}
                >
                  {priceAxisLabelText}
                </span>
              )}
              <div ref={pulseYAxisChartRef} className="h-full w-full min-h-0">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={plotData}
                  margin={{
                    top: gutterPlotMarginsY.top,
                    right: 2,
                    left: compactYAxis
                      ? CHART_SCROLL_Y_AXIS_GUTTER_LEFT_MARGIN_PHONE
                      : CHART_SCROLL_Y_AXIS_GUTTER_LEFT_MARGIN,
                    bottom: gutterPlotMarginsY.bottom,
                  }}
                >
                  <XAxis
                    dataKey="sortKey"
                    type="number"
                    scale="time"
                    domain={xAxisConfig.domain}
                    ticks={xAxisConfig.ticks}
                    hide
                    height={60}
                    interval={0}
                    minTickGap={0}
                  />
                  <YAxis
                    width={priceYAxisWidth}
                    stroke={theme.axisStroke}
                    tick={({ x, y, payload }) => {
                      const label = formatPriceTick(payload.value);
                      return (
                        <text x={x} y={y} textAnchor="end" dominantBaseline="middle" fill={theme.tickFill} fontSize={fontSize}>
                          {label}
                        </text>
                      );
                    }}
                    tickCount={Y_AXIS_TICK_COUNT}
                    domain={priceYDomain}
                    allowDataOverflow={false}
                  />
                  <Legend
                    verticalAlign="bottom"
                    wrapperStyle={{
                      fontSize,
                      paddingTop: 20,
                      height: LEGEND_HEIGHT_PX,
                      overflow: 'hidden',
                      visibility: 'hidden',
                      // Marker consumed by the mobile legend-wrap CSS rule (B4) so it can
                      // exclude this hidden axis-sync gutter legend — its fixed height must
                      // survive at every breakpoint or the gutter's Y axis drifts from the
                      // main chart's. See index.css `.recharts-legend-wrapper` mobile rule.
                      '--chart-legend-gutter': 1,
                    }}
                  />
                  {lineKeys[0] ? (
                    <Line
                      key="y-axis-proxy"
                      type="monotone"
                      dataKey={lineKeys[0]}
                      stroke="transparent"
                      strokeWidth={0}
                      dot={false}
                      activeDot={false}
                      connectNulls
                      isAnimationActive={false}
                    />
                  ) : null}
                </LineChart>
              </ResponsiveContainer>
              </div>
            </div>
          ) : null}
          <div className="flex-1 min-w-0 min-h-0 flex flex-col" ref={plotAreaRef}>
          {plotData.length > 0 && lineKeys.length > 0 ? (
            <div
              className={fitXToWidth ? 'h-full min-h-0 overflow-hidden' : 'chart-x-scroll h-full min-h-0 overflow-x-auto overflow-y-hidden'}
              ref={scrollViewportRef}
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#666 transparent',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <div
                style={{
                  width: needsScroll ? chartWidth : '100%',
                  minWidth: needsScroll ? effectiveMinWidth : undefined,
                  height: '100%',
                }}
              >
                <div
                  ref={needsScroll ? pulseGridChartRef : undefined}
                  className="h-full w-full min-h-0"
                  onPointerDown={markTapStart}
                >
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={plotData}
                    margin={getResponsiveChartMargin({ isBelowLg, isPhone: compactYAxis, needsScroll, right: mainChartRightMargin })}
                    onClick={pinTooltip}
                    onTouchEnd={pinTooltip}
                  >
                    {/* Grid first: vertical segment length is measured and used to match gutter Y-axis spine (recharts-cartesian-axis-line). */}
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.gridColor} vertical horizontal />
                    <XAxis
                      dataKey="sortKey"
                      type="number"
                      scale="time"
                      domain={xAxisConfig.domain}
                      ticks={fitXToWidth ? undefined : xAxisConfig.ticks}
                      stroke={theme.axisStroke}
                      angle={-45}
                      textAnchor="end"
                      height={PULSE_X_AXIS_HEIGHT}
                      interval={fitXToWidth ? 'preserveStartEnd' : 0}
                      minTickGap={fitXToWidth ? 8 : 0}
                      tick={{ fontSize, fill: theme.tickFill }}
                      tickFormatter={(v) => {
                        if (v == null) return '';
                        const d = new Date(Number(v));
                        if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
                        return formatDateShort(d);
                      }}
                    />
                    {needsScroll ? (
                      <YAxis
                        hide
                        width={0}
                        tick={{ fontSize, fill: theme.tickFill }}
                        domain={priceYDomain}
                        tickCount={Y_AXIS_TICK_COUNT}
                        allowDataOverflow={false}
                      />
                    ) : (
                      <YAxis
                        width={priceYAxisWidth}
                        stroke={theme.axisStroke}
                        tick={({ x, y, payload }) => {
                          const label = formatPriceTick(payload.value);
                          return (
                            <text x={x} y={y} textAnchor="end" dominantBaseline="middle" fill={theme.tickFill} fontSize={fontSize}>
                              {label}
                            </text>
                          );
                        }}
                        tickCount={Y_AXIS_TICK_COUNT}
                        domain={priceYDomain}
                        allowDataOverflow={false}
                        label={
                          forceFiltersOpen
                            ? undefined
                            : {
                                value: priceAxisLabelText,
                                angle: -90,
                                position: 'insideLeft',
                                dx: -12,
                                fill: theme.axisLabelFill,
                                fontSize,
                              }
                        }
                      />
                    )}
                    {hasVolumeOverlay ? (
                      <YAxis
                        yAxisId="volume"
                        orientation="right"
                        width={PULSE_VOLUME_Y_AXIS_WIDTH}
                        stroke={PULSE_VOLUME_STROKE_COLOR}
                        tick={{ fontSize, fill: PULSE_VOLUME_STROKE_COLOR }}
                        tickFormatter={(v) => formatLargeNumber(Number(v))}
                        tickCount={Y_AXIS_TICK_COUNT}
                        domain={volumeYDomain}
                        allowDataOverflow={false}
                        tickMargin={4}
                        scale="linear"
                        label={
                          forceFiltersOpen
                            ? undefined
                            : {
                                value: t('filters.volumeLbs'),
                                angle: 90,
                                position: 'insideRight',
                                dx: 8,
                                fill: theme.axisLabelFill,
                                fontSize,
                              }
                        }
                      />
                    ) : null}
                    <Tooltip
                      key={tooltipDismissKey}
                      trigger={tooltipTrigger}
                      active={tooltipActive}
                      position={tooltipPosition}
                      isAnimationActive={false}
                      throttleDelay={50}
                      // Keep null entries so the body can read the row for the weekly-volume
                      // fallback on days with no price data; the body filters nulls itself.
                      filterNull={false}
                      content={linePriceTooltipContent}
                      wrapperStyle={{ pointerEvents: 'auto', maxWidth: 'min(92vw, 28rem)' }}
                      cursor={tooltipCursorX == null}
                    />
                    {tooltipCursorX != null ? (
                      <ReferenceLine x={tooltipCursorX} stroke={theme.tickFill} strokeWidth={1} strokeDasharray="3 3" ifOverflow="extendDomain" />
                    ) : null}
                    {tooltipCursorX != null
                      ? (tooltipPinPayload || []).map((entry) => {
                          if (entry == null || entry.value == null || Number.isNaN(Number(entry.value))) return null;
                          const isVolume = entry.dataKey === PULSE_VOLUME_DATA_KEY;
                          return (
                            <ReferenceDot
                              key={String(entry.dataKey)}
                              x={tooltipCursorX}
                              y={entry.value}
                              r={4}
                              fill={entry.color}
                              stroke={entry.color}
                              ifOverflow="extendDomain"
                              {...(isVolume ? { yAxisId: 'volume' } : {})}
                            />
                          );
                        })
                      : null}
                    {hideLegend ? null : (
                      <Legend
                        verticalAlign="bottom"
                        wrapperStyle={{
                          fontSize,
                          paddingTop: 20,
                          height: 0,
                          overflow: 'hidden',
                          visibility: 'hidden',
                        }}
                      />
                    )}
                    {hasVolumeOverlay ? (
                      <Area
                        yAxisId="volume"
                        type="monotone"
                        dataKey={PULSE_VOLUME_DATA_KEY}
                        name={t('charts.pricePulse.volumeSeriesName')}
                        stroke={PULSE_VOLUME_STROKE_COLOR}
                        fill={PULSE_VOLUME_FILL_COLOR}
                        fillOpacity={PULSE_VOLUME_FILL_OPACITY}
                        strokeWidth={2}
                        baseValue={0}
                        dot={false}
                        activeDot={{ r: 5, fill: PULSE_VOLUME_STROKE_COLOR, stroke: PULSE_VOLUME_STROKE_COLOR }}
                        connectNulls
                        isAnimationActive={false}
                      />
                    ) : null}
                    {lineKeys.map((lineKey, index) => (
                      <Line
                        key={lineKey}
                        type="monotone"
                        dataKey={lineKey}
                        name={getLineDisplayName(lineKey)}
                        stroke={pulseLineColor(lineKey)}
                        strokeWidth={2}
                        strokeDasharray={undefined}
                        strokeLinecap="round"
                        dot={false}
                        activeDot={{ r: 4 }}
                        connectNulls
                        isAnimationActive={false}
                      />
                    ))}
                  </ComposedChart>
                </ResponsiveContainer>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm" style={{ color: theme.secondaryText }}>
                {selectedOrigins.length === 0 || selectedPriceTypes.length === 0
                  ? t('charts.selectAtLeastOneRegion')
                  : t('charts.noMatchingFilters')}
              </p>
            </div>
          )}
          </div>
      </div>
        </div>
        {/* Legend. The Fullscreen action used to live on this row; it now sits
            beside the filter strip at the top of the card. */}
        {plotData.length > 0 && lineKeys.length > 0 ? (() => {
          const items = (
            <>
              {lineKeys.map((lineKey, index) => {
                const color = pulseLineColor(lineKey);
                return (
                  <div key={lineKey} className="flex min-w-0 items-start gap-2 max-lg:w-full lg:shrink-0" role="listitem">
                    <svg width={24} height={10} className="shrink-0 block" aria-hidden>
                      <line
                        x1={2}
                        y1={5}
                        x2={22}
                        y2={5}
                        stroke={color}
                        strokeWidth={2}
                        strokeLinecap="round"
                      />
                    </svg>
                    <span
                      className="min-w-0 whitespace-normal break-words text-left leading-snug"
                      style={{ fontSize: Math.max(fontSize - 1, 10), color: theme.tickFill }}
                    >
                      {getLineDisplayName(lineKey)}
                    </span>
                  </div>
                );
              })}
              {hasVolumeOverlay ? (
                <div className="flex min-w-0 items-start gap-2 max-lg:w-full lg:shrink-0" role="listitem">
                  <svg width={24} height={10} className="shrink-0 block" aria-hidden>
                    <rect
                      x={2}
                      y={1}
                      width={20}
                      height={8}
                      rx={1}
                      fill={PULSE_VOLUME_FILL_COLOR}
                      fillOpacity={PULSE_VOLUME_FILL_OPACITY}
                      stroke={PULSE_VOLUME_STROKE_COLOR}
                      strokeWidth={1.5}
                    />
                  </svg>
                  <span
                    className="min-w-0 whitespace-normal break-words text-left leading-snug"
                    style={{ fontSize: Math.max(fontSize - 1, 10), color: theme.tickFill }}
                  >
                    {t('charts.pricePulse.volumeSeriesName')}
                  </span>
                </div>
              ) : null}
            </>
          );
          if (hideLegend && presetDescription) {
            return <PresetChartLegend theme={theme} fullscreen={presetSingleChartFullscreen}>{items}</PresetChartLegend>;
          }
          if (!hideLegend) {
            return (
              <div className={`flex-shrink-0 px-4 pt-1 ${forceFiltersOpen && !presetSingleChartFullscreen ? '' : 'pb-3'}`}>
                <div className="flex min-w-0 flex-wrap items-center justify-start gap-x-5 gap-y-2" role="list">
                  {items}
                </div>
              </div>
            );
          }
          return null;
        })() : null}
        {seriesCapped ? (
          <p className={`flex-shrink-0 px-4 text-xs ${forceFiltersOpen && !presetSingleChartFullscreen ? '' : 'pb-2'}`} style={{ color: theme.secondaryText }}>
            Showing {lineKeys.length} of {seriesTotal} series. Narrow Origin, Size, or Properties to see the rest.
          </p>
        ) : null}
        </div>
      </div>

      {/* Modals */}
      <FilterModal
        isOpen={priceModalOpen}
        onClose={() => setPriceModalOpen(false)}
        title={t('filters.selectPriceType')}
        options={['low_price', 'high_price']}
        selectedValues={selectedPriceTypes}
        onSelect={handlePriceTypeChange}
        multiSelect
        onSelectAll={() => setSelectedPriceTypes(['low_price', 'high_price'])}
        onClearAll={() => setSelectedPriceTypes([])}
        displayFormatter={(option) => option === 'low_price' ? t('filters.lowPrice') : t('filters.highPrice')}
      />

      <FilterModal
        isOpen={typeModalOpen}
        onClose={() => setTypeModalOpen(false)}
        title={t('filters.selectType')}
        options={availableOptions.types.length > 0 ? availableOptions.types : ['Conventional', 'Organic']}
        selectedValues={selectedType}
        onSelect={(value) => {
          setSelectedType(value);
          cascadeBelow('type', { type: value });
        }}
      />

      <FilterModal
        isOpen={varietyModalOpen}
        onClose={() => setVarietyModalOpen(false)}
        title={t('filters.selectVariety')}
        options={availableOptions.varieties}
        selectedValues={selectedVariety}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedVariety, VARIETY_SELECT_MAX);
          const fallback = defaultPulseVarieties(data);
          const val = next.length ? next : fallback;
          setSelectedVariety(val);
          cascadeBelow('variety', { variety: val });
        }}
        multiSelect
        onSelectAll={() => {
          const val = defaultPulseVarieties(data);
          setSelectedVariety(val);
          cascadeBelow('variety', { variety: val });
        }}
        onClearAll={() => {
          const val = defaultPulseVarieties(data);
          setSelectedVariety(val);
          cascadeBelow('variety', { variety: val });
        }}
      />

      <FilterModal
        isOpen={originModalOpen}
        onClose={() => {
          setOriginModalOpen(false);
          cascadeFromOrigins();
        }}
        title={t('filters.selectOrigins')}
        options={availableOptions.origins}
        selectedValues={selectedOrigins}
        onSelect={handleOriginChange}
        multiSelect
        onSelectAll={() => {
          const allOrigins = availableOptions.origins;
          setSelectedOrigins(allOrigins);
          setOriginsUserCleared(false);
          cascadeFromOrigins(allOrigins);
        }}
        onClearAll={() => {
          setSelectedOrigins([]);
          setOriginsUserCleared(true);
        }}
      />

      {sizeRules && availableOptions.sizeGroups.length > 0 && (
      <FilterModal
        isOpen={sizeGroupModalOpen}
        onClose={() => setSizeGroupModalOpen(false)}
        title={t('filters.sizeGroup', 'Size Group')}
        options={availableOptions.sizeGroups}
        selectedValues={selectedSizeGroup}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedSizeGroup);
          setSelectedSizeGroup(next);
          cascadeBelow('sizeGroup', { sizeGroup: next });
        }}
        multiSelect
        onSelectAll={() => {
          setSelectedSizeGroup(availableOptions.sizeGroups);
          cascadeBelow('sizeGroup', { sizeGroup: availableOptions.sizeGroups });
        }}
        onClearAll={() => {
          setSelectedSizeGroup([]);
          cascadeBelow('sizeGroup', { sizeGroup: [] });
        }}
      />
      )}

      <FilterModal
        isOpen={sizeModalOpen && availableOptions.sizes.length > 0}
        onClose={() => setSizeModalOpen(false)}
        title={sizeRules ? t('filters.sizeGranularity', 'Size granularity') : t('filters.selectSize')}
        options={availableOptions.sizes}
        selectedValues={selectedSize}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedSize);
          const val = sizeRules ? next : (next.length ? next : availableOptions.sizes);
          setSelectedSize(val);
          cascadeBelow('size', { size: val });
        }}
        multiSelect
        onSelectAll={() => {
          setSelectedSize(availableOptions.sizes);
          cascadeBelow('size', { size: availableOptions.sizes });
        }}
        onClearAll={() => {
          if (sizeRules) {
            setSelectedSize([]);
            cascadeBelow('size', { size: [] });
            return;
          }
          setSelectedSize(availableOptions.sizes);
          cascadeBelow('size', { size: availableOptions.sizes });
        }}
      />

      <FilterModal
        isOpen={gradeModalOpen}
        onClose={() => setGradeModalOpen(false)}
        title={t('filters.selectGrade')}
        options={availableOptions.grades}
        selectedValues={selectedGrade}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedGrade);
          const val = next.length ? next : availableOptions.grades;
          setSelectedGrade(val);
          cascadeBelow('grade', { grade: val });
        }}
        multiSelect
        onSelectAll={() => {
          setSelectedGrade(availableOptions.grades);
          cascadeBelow('grade', { grade: availableOptions.grades });
        }}
        onClearAll={() => {
          setSelectedGrade(availableOptions.grades);
          cascadeBelow('grade', { grade: availableOptions.grades });
        }}
      />

      <FilterModal
        isOpen={packageModalOpen}
        onClose={() => setPackageModalOpen(false)}
        title={t('filters.selectSku')}
        options={availableOptions.packages}
        selectedValues={selectedPackage}
        onSelect={(value) => {
          setSelectedPackage(value);
          cascadeBelow('package', { package: value });
        }}
      />

      <FilterModal
        isOpen={priceUnitModalOpen}
        onClose={() => setPriceUnitModalOpen(false)}
        title={t('filters.priceUnit')}
        options={['package', 'kg', 'lbs']}
        selectedValues={priceUnit}
        onSelect={(value) => {
          if (value === priceUnit) return;
          setPriceUnit(value);
          if (value === 'package') {
            restorePackageSelection();
          } else {
            if (selectedPackage) lastPackageRef.current = selectedPackage;
            setSelectedPackage('');
          }
        }}
        displayFormatter={(option) => option === 'package' ? t('filters.package') : option === 'kg' ? t('filters.perKg') : t('filters.perLb')}
      />

      <FilterModal isOpen={storageModalOpen} onClose={() => setStorageModalOpen(false)} title="Select Storage"
        options={availableOptions.storages} selectedValues={selectedStorage}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedStorage);
          const val = next.length ? next : availableOptions.storages;
          setSelectedStorage(val);
          cascadeBelow('storage', { storage: val });
        }}
        multiSelect
        onSelectAll={() => { setSelectedStorage(availableOptions.storages); cascadeBelow('storage', { storage: availableOptions.storages }); }}
        onClearAll={() => { setSelectedStorage(availableOptions.storages); cascadeBelow('storage', { storage: availableOptions.storages }); }}
      />
      <FilterModal isOpen={conditionModalOpen} onClose={() => setConditionModalOpen(false)} title="Select Condition"
        options={availableOptions.conditions} selectedValues={selectedCondition}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedCondition);
          const val = next.length ? next : availableOptions.conditions;
          setSelectedCondition(val);
          cascadeBelow('condition', { condition: val });
        }}
        multiSelect
        onSelectAll={() => { setSelectedCondition(availableOptions.conditions); cascadeBelow('condition', { condition: availableOptions.conditions }); }}
        onClearAll={() => { setSelectedCondition(availableOptions.conditions); cascadeBelow('condition', { condition: availableOptions.conditions }); }}
      />
      <FilterModal isOpen={environmentModalOpen} onClose={() => setEnvironmentModalOpen(false)} title="Select Environment"
        options={availableOptions.environments} selectedValues={selectedEnvironment}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedEnvironment);
          const val = next.length ? next : availableOptions.environments;
          setSelectedEnvironment(val);
          cascadeBelow('environment', { environment: val });
        }}
        multiSelect
        onSelectAll={() => { setSelectedEnvironment(availableOptions.environments); cascadeBelow('environment', { environment: availableOptions.environments }); }}
        onClearAll={() => { setSelectedEnvironment(availableOptions.environments); cascadeBelow('environment', { environment: availableOptions.environments }); }}
      />
      <FilterModal isOpen={qualityModalOpen} onClose={() => setQualityModalOpen(false)} title="Select Quality"
        options={availableOptions.qualities} selectedValues={selectedQuality}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedQuality);
          const val = next.length ? next : availableOptions.qualities;
          setSelectedQuality(val);
          cascadeBelow('quality', { quality: val });
        }}
        multiSelect
        onSelectAll={() => { setSelectedQuality(availableOptions.qualities); cascadeBelow('quality', { quality: availableOptions.qualities }); }}
        onClearAll={() => { setSelectedQuality(availableOptions.qualities); cascadeBelow('quality', { quality: availableOptions.qualities }); }}
      />
      <FilterModal isOpen={appearanceModalOpen} onClose={() => setAppearanceModalOpen(false)} title="Select Appearance"
        options={availableOptions.appearances} selectedValues={selectedAppearance}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedAppearance);
          const val = next.length ? next : availableOptions.appearances;
          setSelectedAppearance(val);
          cascadeBelow('appearance', { appearance: val });
        }}
        multiSelect
        onSelectAll={() => { setSelectedAppearance(availableOptions.appearances); cascadeBelow('appearance', { appearance: availableOptions.appearances }); }}
        onClearAll={() => { setSelectedAppearance(availableOptions.appearances); cascadeBelow('appearance', { appearance: availableOptions.appearances }); }}
      />
      <FilterModal isOpen={propertiesModalOpen} onClose={() => setPropertiesModalOpen(false)} title="Select Properties"
        options={availableOptions.properties} selectedValues={selectedProperties}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedProperties);
          const val = next.length ? next : availableOptions.properties;
          setSelectedProperties(val);
          cascadeBelow('properties', { properties: val });
        }}
        multiSelect
        onSelectAll={() => { setSelectedProperties(availableOptions.properties); cascadeBelow('properties', { properties: availableOptions.properties }); }}
        onClearAll={() => { setSelectedProperties(availableOptions.properties); cascadeBelow('properties', { properties: availableOptions.properties }); }}
      />
    </div>
  );
};

export default PriceLineChart;
